import Controller from "csi/action/Controller";
import { resolveNodesWithDependencies } from "csi/service/nodeService";
import { open3dViewer } from "csi/service/rightPaneService";
import ThreeDStore, { SnapshotSaveLocationData } from "csi/state/3D/threeDViewerStore";
import { routerStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import declare from "dojo/_base/declare";
import { filterSnapshotSaveNodes, getSnapshotSuggestedNodes } from "./utility/snapshotsSuggestedTags";

const ThreeDViewerController = declare(Controller, {

	/** @property {C8Node} docNode - The document node to get the 3D document from. */
	docNode: null,

	/**
	 * @override
	 * Ensure access to colorways
	 */
	onDependencyPaths(paths: string[]): void {
		paths.push("Child:__Parent__/Child:ActiveColorways");
	},
	
	/**
	 * Refresh table data after upload snapshot
	 */
	onUploadSnapshot(snapshotData) {
		this.widget.refreshData();
	},

	/** 
	 * Return the document node
	 */
	getDocument(): C8Node {
		return this.getNode() || this.docNode;
	},

	/**
	 * Return the Style node
	 */
	async getStyleNode(): Promise<C8Node> {
		const document = this.getDocument();
		const [node] = await resolveNodesWithDependencies([document.__Parent__], ["Child:__Parent__/Child:ActiveColorways"], this.m_cache);
		const parentNode = this.m_cache.get(node.__Parent__);
		return (parentNode?.$Type === "Style") ? parentNode : null;
	},

	/**
	 * Return Active Collorways from a style node
	 */
	getActiveColorways(styleNode: C8Node): C8Node[] {
		return this.m_cache.getNodes(styleNode?.ActiveColorways) || [];
	},

	/** Return the Nodes that a snapshot can be saved to */
	async getSnapshotSaveNodes(revisionNode: C8Node, document: C8Node): Promise<SnapshotSaveLocationData> {
		const style = await this.getStyleNode();
		const colorways = this.getActiveColorways(style);
		const { locationType } = routerStore;
		
		const locations = filterSnapshotSaveNodes([
			revisionNode,
			...(style ? [style, ...colorways] : []),
		]);

		if (!locations.length) {
			return {
				locations: [],
				suggestedLocations: [],
			};
		}

		const suggestedLocations = style 
			? getSnapshotSuggestedNodes(style, colorways, document, locationType)
			: [];
	
		return {
			locations,
			suggestedLocations,
		};
	},

	/**
	 * Return view definition for 3D viewer
	 */
	async _onAction() {
		const document = this.getDocument();
		const [styleNode, [revision, variantSource]] = await Promise.all([
			this.getStyleNode(),
			resolveNodesWithDependencies([document.CurrentRevision, document.VariantSource], null, this.m_cache),
		]);
		
		const variantName = document.VariantName;
		const variantSourceName = variantSource?.$Name;
		
		const threeDTitle = variantSourceName || document.$Name;
		const viewerType = "3DViewer";
		const productType = styleNode?.ProductType;
		const rootNodeType = styleNode?.$Type;
		const snapshotSaveLocationsData = await this.getSnapshotSaveNodes(revision, document);
		const threeDStore = new ThreeDStore(document, revision, this.onUploadSnapshot.bind(this), snapshotSaveLocationsData, viewerType, this.m_cache, threeDTitle, variantName, productType, rootNodeType);
		open3dViewer(threeDStore);
	},
});

export = ThreeDViewerController;

